import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getBlobAndBufferAndContentType } from './Utils';

/**
 * Renders an <img> for sources that require an Authorization header (e.g.
 * `/document/fetch-document-content?...`). A plain <img src> cannot send the
 * auth header, so such URLs render broken. This component fetches the bytes
 * with the auth token (via getBlobAndBufferAndContentType) and renders the
 * resulting object URL instead. Direct/public URLs still work as-is.
 *
 * Accepts and forwards any standard <img> props (className, style, alt, ...).
 */
const AuthedImage = ({ src, ...imgProps }) => {
  const authToken = useSelector((state) => state?.user?.authToken);
  const [resolvedSrc, setResolvedSrc] = useState(null);

  useEffect(() => {
    let active = true;
    let objectUrl = null;

    if (!src) {
      setResolvedSrc(null);
      return undefined;
    }

    (async () => {
      try {
        const { blobUrl } = await getBlobAndBufferAndContentType({
          fileUrl: src,
          headers: { Authorization: authToken },
        });
        if (!active) {
          if (blobUrl && blobUrl !== src) URL.revokeObjectURL(blobUrl);
          return;
        }
        objectUrl = blobUrl && blobUrl !== src ? blobUrl : null;
        setResolvedSrc(blobUrl || src);
      } catch (error) {
        if (active) setResolvedSrc(src);
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, authToken]);

  return <img src={resolvedSrc || src} {...imgProps} />;
};

export default AuthedImage;
